import type { NTable } from '../interface';

const INTERNAL_KEY_PREFIX = 'CDS_TABLE_KEY';
export function mergeObject<ReturnObject extends object>(...objects: Partial<ReturnObject>[]): ReturnObject {
  const merged: Partial<ReturnObject> = {};

  function fillProps(obj: object | any, clone: object | any) {
    if (clone) {
      Object.keys(clone).forEach((key) => {
        const value = clone[key];
        if (value && typeof value === 'object') {
          obj[key] = obj[key] || {};
          fillProps(obj[key], value);
        } else {
          obj[key] = value;
        }
      });
    }
  }

  objects.forEach((clone) => {
    fillProps(merged, clone);
  });

  return merged as ReturnObject;
}

export function getPathValue<ValueType, ObjectType extends object>(
  record: ObjectType,
  path?: NTable.TDataIndex
): ValueType | null {
  // Skip if path is empty
  if (!path && typeof path !== 'number') {
    return record as unknown as ValueType;
  }

  const pathList = toArray(path);

  let current: ValueType | ObjectType = record;

  for (let i = 0; i < pathList.length; i += 1) {
    if (!current) {
      return null;
    }

    const prop = pathList[i];
    current = (current as ObjectType | ValueType | any)[prop];
  }

  return current as ValueType;
}

interface TGetColumnKeyColumn {
  key?: NTable.TKey;
  dataIndex?: NTable.TDataIndex;
}
export function getColumnsKey(columns: readonly TGetColumnKeyColumn[]) {
  const columnKeys: NTable.TKey[] = [];
  const keys: Record<NTable.TKey, boolean> = {};

  columns.forEach((column) => {
    const { key, dataIndex } = column || {};

    let mergedKey = key || toArray(dataIndex).join('-') || INTERNAL_KEY_PREFIX;
    while (keys[mergedKey]) {
      mergedKey = `${mergedKey}_next`;
    }
    keys[mergedKey] = true;

    columnKeys.push(mergedKey);
  });

  return columnKeys;
}

export function validateValue<T>(value: T) {
  return value !== null && value !== undefined;
}

function toArray<T>(arr: T | readonly T[]): T[] {
  if (arr === undefined || arr === null) {
    return [];
  }
  return (Array.isArray(arr) ? arr : [arr]) as T[];
}
